import { Navigate, Outlet } from "react-router-dom";

import { Root } from "@/layouts";
import { COMMON, SPOWNER, TDCLIENT, TDADMIN } from "@/pages";
import { RequirePage } from "../protection";
import { FirstAllowedTabRedirect } from "../protection/first-allowed-tab-redirect";
import {
  COMPLIANCE_TAB_KEYS,
  TOOLS_TAB_KEYS,
  POTENTIAL_TAB_KEYS,
} from "@/constants";

const toTabList = (keys: Record<string, string>) =>
  Object.entries(keys).map(([link, pageKey]) => ({ link, pageKey }));

const COMMONRoutes = [
  {
    path: "",
    element: <Navigate to={"/dashboard/analytics"} />,
  },
  {
    path: "potential-savings",
    element: <Navigate to="/dashboard/potential" replace />,
  },
  {
    path: "dashboard",
    element: <Root />,
    children: [
      { path: "analytics", element: <RequirePage pageKey="ANALYTICS"><COMMON.Analytics /></RequirePage> },
      { path: "validate/:id", element: <COMMON.FirstSaleValidate /> },
      {
        path: "potential",
        element: <RequirePage pageKey="POTENTIAL_SAVINGS"><COMMON.Potential /></RequirePage>,
        children: [
          { index: true, element: <FirstAllowedTabRedirect tabs={toTabList(POTENTIAL_TAB_KEYS)} /> },
          { path: "first-sale", element: <Navigate to="/first-sale" replace /> },
          {
            path: "preferential-origin",
            element: <RequirePage pageKey="POTENTIAL_SAVINGS_PREFERENTIAL_ORIGIN"><COMMON.PreferentialOrigin /></RequirePage>,
          },
          {
            path: "ftz",
            element: <RequirePage pageKey="POTENTIAL_SAVINGS_FTZ"><COMMON.FTZ /></RequirePage>,
          },
          {
            path: "exclusions",
            element: <RequirePage pageKey="POTENTIAL_SAVINGS_301_EXCLUSIONS"><COMMON.Exclusions /></RequirePage>,
          },
        ],
      },
      { path: "historical", element: <COMMON.Historical /> },
      { path: "risk-and-trends", element: <COMMON.RiskAndTrends /> },
    ],
  },
  {
    path: "chat-advisor",
    element: <Root />,
    children: [{ path: "", element: <RequirePage pageKey="CHAT_ASSISTANCE"><COMMON.ChatAdvisor /></RequirePage> }],
  },
  // NEW: Customs ERP - consolidates Product Hub, Documents, Entries, Suppliers
  {
    path: "customs-erp",
    element: <Root />,
    children: [
      {
        element: <RequirePage pageKey="CUSTOMS_ERP"><COMMON.CustomsERP /></RequirePage>,
        children: [
          { index: true, element: <Navigate to="dashboard" replace /> },
          { path: "dashboard", element: <COMMON.CustomsERPDashboardTab /> },
          { path: "products", element: <RequirePage pageKey="CUSTOMS_ERP_PRODUCTS"><COMMON.CustomsERPProductsTab /></RequirePage> },
          { path: "suppliers", element: <RequirePage pageKey="CUSTOMS_ERP_SUPPLIERS"><COMMON.CustomsERPSuppliersTab /></RequirePage> },
          { path: "entries", element: <RequirePage pageKey="CUSTOMS_ERP_ENTRIES"><COMMON.CustomsERPEntriesTab /></RequirePage> },
          { path: "documents", element: <RequirePage pageKey="CUSTOMS_ERP_DOCUMENTS"><COMMON.CustomsERPDocumentsTab /></RequirePage> },
        ],
      },
    ],
  },
  // NEW: Compliance Hub - Tariff Analysis, Post Import Adjustment
  {
    path: "compliance",
    element: <Root />,
    children: [
      {
        element: <RequirePage pageKey="COMPLIANCE"><COMMON.Compliance /></RequirePage>,
        children: [
          { index: true, element: <FirstAllowedTabRedirect tabs={toTabList(COMPLIANCE_TAB_KEYS)} /> },
          { path: "verification", element: <RequirePage pageKey="COMPLIANCE_VERIFICATION"><COMMON.ComplianceVerificationTab /></RequirePage> },
          { path: "tariff-analysis", element: <RequirePage pageKey="COMPLIANCE_TARIFF_ANALYSIS"><COMMON.ComplianceTariffAnalysis /></RequirePage> },
          { path: "post-import", element: <RequirePage pageKey="COMPLIANCE_POST_IMPORT"><COMMON.CompliancePostImport hideHeader /></RequirePage> },
          { path: "certificate-of-origin", element: <RequirePage pageKey="COMPLIANCE_CERTIFICATE_OF_ORIGIN"><COMMON.CertificateOfOrigin /></RequirePage> },
          {
            path: "tariff-engineering",
            element: <RequirePage pageKey="COMPLIANCE_TARIFF_ENGINEERING"><Outlet /></RequirePage>,
            children: [
              { index: true, element: <COMMON.TariffEngineering /> },
              { path: "products/:productId", element: <COMMON.TariffEngineeringProductDetail /> },
              { path: ":htsCode/:countryOfOrigin", element: <COMMON.TariffEngineeringDetail /> },
            ],
          },
        ],
      },
      {
        path: "create-adjustment",
        element: <RequirePage pageKey="COMPLIANCE"><COMMON.CreateAdjustment /></RequirePage>
      },
    ],
  },
  // NEW: Tools - Duty Calculator, Unit Cost Analysis, Commerce Data, Scenario Modeling
  {
    path: "tools",
    element: <Root />,
    children: [
      {
        element: <RequirePage pageKey="TOOLS"><COMMON.Tools /></RequirePage>,
        children: [
          { index: true, element: <FirstAllowedTabRedirect tabs={toTabList(TOOLS_TAB_KEYS)} /> },
          { path: "duty-calculator", element: <RequirePage pageKey="TOOLS_DUTY_CALCULATOR"><COMMON.ToolsDutyCalculator /></RequirePage> },
          { path: "unit-cost-analysis", element: <RequirePage pageKey="TOOLS_UNIT_COST_ANALYSIS"><COMMON.ToolsUnitCostAnalysis /></RequirePage> },
          { path: "commerce-data", element: <RequirePage pageKey="TOOLS_COMMERCE_DATA"><COMMON.ToolsCommerceData /></RequirePage> },
          { path: "scenario-modeling", element: <RequirePage pageKey="TOOLS_SCENARIO_MODELING"><COMMON.ToolsScenarioModeling /></RequirePage> },
          { path: "ieepa-refund-cape", element: <RequirePage pageKey="TOOLS_IEEPA_REFUND_CAPE"><COMMON.ToolsIeepaRefundCape /></RequirePage> },
          { path: "ieepa-refund-cape/:reportId", element: <RequirePage pageKey="TOOLS_IEEPA_REFUND_CAPE"><COMMON.ToolsIeepaRefundCapeDetail /></RequirePage> },
        ],
      },
    ],
  },
  // NEW: Duty Columns - standalone page for duty column management (admins only)
  {
    path: "duty-columns",
    element: <Root />,
    children: [{
      path: "",
      element: <RequirePage pageKey="PRODUCT_HUB_DUTY_COLUMNS"><COMMON.DutyColumns /></RequirePage>
    }],
  },
  // NEW: First Sale Program - standalone page with sub-routes
  {
    path: "first-sale",
    element: <Root />,
    children: [
      {
        path: "",
        element: <RequirePage pageKey="FIRST_SALE"><COMMON.FirstSaleProgram /></RequirePage>,
      },
      {
        path: "supplier/:supplierId",
        element: <RequirePage pageKey="FIRST_SALE"><COMMON.FirstSaleSupplierDetail /></RequirePage>,
      },
      {
        path: "case/:caseId",
        element: <RequirePage pageKey="FIRST_SALE"><COMMON.FirstSaleCaseDetail /></RequirePage>,
      },
      {
        path: "case/:caseId/supplier-portal",
        element: <RequirePage pageKey="FIRST_SALE"><COMMON.FirstSaleSupplierPortal /></RequirePage>,
      },
    ],
  },
  // DEPRECATED ROUTES - Redirect to new consolidated pages
  { path: "dashboard/potential/first-sale", element: <Navigate to="/first-sale" replace /> },
  { path: "product-catalogue", element: <Navigate to="/customs-erp" replace /> },
  { path: "duty-calculator", element: <Navigate to="/tools" replace /> },
  { path: "trade-data", element: <Navigate to="/compliance" replace /> },
  { path: "documents", element: <Navigate to="/customs-erp" replace /> },
  { path: "documents/*", element: <Navigate to="/customs-erp" replace /> },
  { path: "post-import-adjustment", element: <Navigate to="/compliance" replace /> },
  { path: "scenario-modeling", element: <Navigate to="/tools" replace /> },
  {
    path: "settings",
    element: <COMMON.Settings />,
    children: [
      { index: true, element: <Navigate to="account-settings" replace /> },
      { path: "account-settings", element: <COMMON.AccountSettings /> },
      { path: "changelog", element: <COMMON.Changelog /> },
      { path: "validation-checks", element: <COMMON.ValidationChecksSettings /> },
    ],
  },
  {
    path: "help-center",
    element: <Root />,
    children: [{ path: "", element: <COMMON.HelpCenter /> }],
  },
];

const SPOWNERRoutes = [
  ...COMMONRoutes,
  {
    path: "partners",
    element: <Root />,
    children: [{ path: "", element: <SPOWNER.SPOWNERPartners /> }],
  },
  {
    path: "clients",
    element: <Root />,
    children: [{ path: "", element: <SPOWNER.SPOWNERClients /> }],
  },
];

const SPPARTNERRoutes = [...COMMONRoutes];

const SPCLIENTRoutes = [...COMMONRoutes];

const TDCLIENTRoutes = [
  ...COMMONRoutes,
  {
    path: "users",
    element: <Root />,
    children: [{ path: "", element: <TDCLIENT.TDClIENTUsers /> }],
  },
];

const TDADMINRoutes = [
  ...COMMONRoutes,
  {
    path: "users",
    element: <Navigate to="/tenant-management" replace />,
  },
  {
    path: "/tenant-management",
    element: <Root />,
    children: [{ path: "", element: <TDADMIN.TDADMINTenantManagement /> }],
  },
  {
    path: "/refunds-analytics",
    element: <Root />,
    children: [{ path: "", element: <TDADMIN.TDADMINRefundsAnalytics /> }],
  },
];

const TDADMIN_SPRoutes = [
  {
    path: "partners",
    element: <Root />,
    children: [{ path: "", element: <SPOWNER.SPOWNERPartners /> }],
  },
  {
    path: "clients",
    element: <Root />,
    children: [{ path: "", element: <SPOWNER.SPOWNERClients /> }],
  },
];

export const RolesRoutes = {
  SPOWNER: [...SPOWNERRoutes],
  SPPARTNER: [...SPPARTNERRoutes],
  SPCLIENT: [...SPCLIENTRoutes],
  TDCLIENT: [...TDCLIENTRoutes],
  TDADMIN: [...TDADMINRoutes],
  TDADMIN_SP: [...TDADMIN_SPRoutes],
};
